import React, {useCallback, Fragment, useEffect} from "react";
import styled from "styled-components";
import {NavLink} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {LANGUAGE_PATH, trackAdobeAnalytics, useMediaQuery} from "modules";
import {media} from "assets/css/media";
import {handleModalState} from "apps/DRAW_PREDICTOR/modules/actions/modals";
import {useDispatch, useSelector} from "react-redux";
import {Exist, ExternalLink, PureButton} from "components";
import {LanguageSelector} from "apps/DRAW_PREDICTOR/components/Header/LanguageSelector";
import {ReactComponent as UserIcon} from "assets/images/icons/user.svg";
import {HamburgerMenu} from "./HamburgerMenu";
import {isLoggedIn, isMenuOpen} from "modules/selectors";
import {HeaderFifa} from "apps/DRAW_PREDICTOR/components/Header/HeaderFifa";
import {ReactComponent as Logo} from "assets/images/logos/fifaLogoBlue.svg";
import {IS_PRE_DRAW} from "apps/DRAW_PREDICTOR/modules/constants";
import {ReactComponent as Qatar} from "apps/DRAW_PREDICTOR/assets/images/logos/Qatar-Airways.svg";

const HeaderWrapper = styled.header`
	width: 100%;
	height: 60px;
	position: sticky;
	top: 0;
	z-index: 9999;
	background: #fff;
`;

const Inner = styled.div`
	height: 100%;
	max-width: 1360px;
	margin: 0 auto;
	padding: 16px 20px;
	display: flex;
	align-items: center;
	position: relative;
	.shadow {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 1px;
		box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
	}
	@media (max-width: ${media.mobile}) {
		justify-content: space-between;
		position: relative;
	}
`;

const LogoWrapper = styled.div`
	color: ${({theme}) => theme.global.header_hover_color};
	margin-inline-end: 30px;
	padding-inline-end: 30px;
	border-inline-end: 1px solid #e8e6e2;
	height: 30px;
	text-transform: uppercase;
	display: flex;
	align-items: center;
	a {
		font-family: "Qatar2022";
		font-style: normal;
		font-weight: 700;
		font-size: 14px;
		line-height: 100%;
	}
	@media (max-width: ${media.mobile}) {
		display: none;
	}
`;

const DesktopMenuWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 40px;
	@media (max-width: ${media.mobile}) {
		display: none;
	}
`;

const InternalLink = styled(PureButton)`
	font-family: "Graphik";
	font-weight: 600;
	font-size: 14px;
	line-height: 160%;
	color: #000;
	transition: color 0.2s ease-in-out;
	&:hover {
		color: ${({theme}) => theme.global.header_hover_color};
	}
	&.active {
		color: ${({theme}) => theme.global.header_hover_color};
		&.tutorial {
			color: #000;
		}
	}
`;

const MobileLogo = styled.a`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
`;

interface IMenuProps {
	showTutorial: () => void;
}

const LeftSide = styled.div`
	margin-inline-start: auto;
	display: flex;
	align-items: center;
	gap: 20px;
	@media (max-width: ${media.mobile}) {
		margin-inline-start: 0;
	}
`;

const Item = styled.div`
	&.hidden-mobile {
		@media (max-width: ${media.mobile}) {
			display: none;
		}
	}
	svg path {
		fill: #000;
	}
`;

const LoginButton = styled(InternalLink)`
	text-transform: uppercase;
`;

const SponsorLink = styled(ExternalLink)`
	padding: 0 10px;
	color: #000;
	display: flex;
	align-items: center;
	font-family: "Qatar2022";
	font-weight: 400;
	font-size: 12px;
	line-height: 160%;
	span {
		display: block;
		margin-top: 2px;
	}
`;

const DesktopMenu: React.FC<IMenuProps> = ({showTutorial}) => {
	const {t} = useTranslation(LANGUAGE_PATH);

	if (IS_PRE_DRAW) {
		return null;
	}

	return (
		<DesktopMenuWrapper>
			<InternalLink to="/" as={NavLink}>
				{t("draw.header.my_draw")}
			</InternalLink>
			<InternalLink to="/help/prizes" as={NavLink}>
				{t("draw.header.prizes")}
			</InternalLink>
			<InternalLink to="/help/" as={NavLink}>
				{t("draw.header.help")}
			</InternalLink>
			<InternalLink className="tutorial" as={NavLink} to="/" onClick={showTutorial}>
				{t("draw.header.tutorial")}
			</InternalLink>
		</DesktopMenuWrapper>
	);
};

export const Header: React.FC = () => {
	const {t} = useTranslation(LANGUAGE_PATH);
	const isMobile = useMediaQuery(`(max-width: ${media.mobile})`);
	const dispatch = useDispatch();
	const isOpen = useSelector(isMenuOpen);
	const openClass = isOpen ? "open" : "";
	const isUser = useSelector(isLoggedIn);

	const showTutorial = useCallback(() => {
		dispatch(handleModalState({tour: true}));
	}, [dispatch]);

	const showLogin = () => {
		dispatch(handleModalState({registration: true})); // TODO: Change to login
	};

	const handleSponsorClick = () => {
		trackAdobeAnalytics({
			section: "",
			eventName: "partnerLinkClick",
			optional: {
				partner: "Qatar Airways",
				source: "Instagram",
			},
		});
	};

	useEffect(() => {
		trackAdobeAnalytics({
			section: "",
			eventName: "partnerLinkLoad",
			optional: {
				partner: "Qatar Airways",
				source: "Instagram",
			},
		});
	}, []);

	return (
		<Fragment>
			<Exist when={!isMobile}>
				<HeaderFifa />
			</Exist>
			<HeaderWrapper className={openClass}>
				<Inner>
					<Exist when={isMobile}>
						<HamburgerMenu showTutorial={showTutorial} />
					</Exist>
					<LogoWrapper>
						<NavLink to="/">{t("draw.header.game_name")}</NavLink>
					</LogoWrapper>
					<Exist when={isMobile}>
						<MobileLogo href="https://www.fifa.com/">
							<Logo />
						</MobileLogo>
					</Exist>
					<DesktopMenu showTutorial={showTutorial} />
					<LeftSide>
						<Item className="hidden-mobile">
							<SponsorLink
								href="https://www.instagram.com/qatarairways/"
								onClick={handleSponsorClick}>
								<span>{t("draw.header.follow")}</span> <Qatar />
							</SponsorLink>
						</Item>
						<Item className="hidden-mobile">
							<LanguageSelector />
						</Item>
						<Exist when={!IS_PRE_DRAW}>
							<Item>
								{isUser ? (
									<UserIcon /> /*TODO: add link to user profile*/
								) : (
									<LoginButton onClick={showLogin}>
										{t("account.option.login")}
									</LoginButton>
								)}
							</Item>
						</Exist>
					</LeftSide>
				</Inner>
			</HeaderWrapper>
		</Fragment>
	);
};
